import { useState } from "react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { ContactForm } from "@/components/sections/ContactForm";
import { RelatedLinksSection } from "@/components/sections/RelatedLinksSection";
import {
  ArrowRight,
  Check,
  Wallet,
  Scale,
  Award,
  LifeBuoy,
  FileText,
  Clock,
  ShieldCheck,
  Receipt,
  Plane,
  HeartPulse,
  CalendarDays,
  Building2,
  Briefcase,
  Truck,
  Store,
  Users,
  Plus,
  Minus,
  Phone,
  Quote,
  Sparkles,
  Send,
  BookOpen,
} from "lucide-react";
import hero from "@/assets/hero-loner-dark.webp";
import faqImage from "@/assets/lonead.webp";
import specialistImage from "@/assets/lonead-4.webp";
import storyExperts from "@/assets/lonead-1.webp";
import storyTrust from "@/assets/lonead-2.webp";
import storyBackup from "@/assets/lonead-3.webp";
import { StoryAlternating } from "@/components/sections/StoryAlternating";

const trustItems = [
  "Auktoriserade lönespecialister",
  "Kollektivavtal & arbetsrätt på fingertopparna",
  "Säker rapportering till myndigheter",
  "Lönebackup vid akut frånvaro",
];

const included = [
  {
    icon: Wallet,
    title: "Komplett löneutbetalning varje månad",
    body: "Vi hanterar löneutbetalningar, lönespecifikationer, kontoinläsningar och bankfilshantering. Era anställda får alltid lönen i tid — utan undantag.",
  },
  {
    icon: Clock,
    title: "Tidrapporter, semester & sjukfrånvaro",
    body: "Vi sköter tidrapporter, semesterdagar, sjukskrivningar, föräldraledighet och VAB. Allt registreras korrekt och balanseras mot kollektivavtalet.",
  },
  {
    icon: Receipt,
    title: "Reseräkningar, traktamenten & utlägg",
    body: "Vi hanterar reseräkningar, traktamenten, milersättning, förmåner och utlägg — med korrekta skattemässiga underlag och rätt momsavlyft där det går.",
  },
  {
    icon: Send,
    title: "Rapportering till Skatteverket & myndigheter",
    body: "Arbetsgivardeklaration på individnivå (AGI), kontrolluppgifter, FORA, Collectum, statistik till SCB — vi sköter all rapportering inom utsatt tid.",
  },
  {
    icon: BookOpen,
    title: "Kollektivavtal & arbetsrättslig rådgivning",
    body: "Vi har djup kunskap om Sveriges vanligaste kollektivavtal. Vi reder ut komplexa lönefrågor — från tjänstepension och OB-tillägg till retroaktiva justeringar.",
  },
  {
    icon: FileText,
    title: "Arbetsgivarintyg & anställningsavtal",
    body: "Vi tar fram arbetsgivarintyg, anställningsavtal och lönebesked — alltid korrekt formulerade och redo när era medarbetare behöver dem.",
  },
];

const steps = [
  {
    icon: Briefcase,
    title: "Vi tar över er löneadministration",
    body: "Vi går igenom ert nuläge, anställningsavtal, kollektivavtal och löneunderlag. Vi sätter upp processer, bankkopplingar och rapporteringsrutiner — sömlöst och utan avbrott.",
  },
  {
    icon: CalendarDays,
    title: "Löpande månadshantering enligt avtalad lönecykel",
    body: "Ni skickar in tidrapporter och förändringar. Vi räknar ut lönen, skickar lönespecifikationer, sköter utbetalningar och rapporterar till myndigheter — varje månad, i tid.",
  },
  {
    icon: ShieldCheck,
    title: "Avstämning, support & löpande optimering",
    body: "Era medarbetare och ni har en namngiven kontaktperson hos oss. Vi besvarar lönefrågor, justerar vid behov och föreslår förbättringar löpande.",
  },
];

const forWhom = [
  { icon: Building2, label: "Aktiebolag med 1–100 anställda" },
  { icon: Truck, label: "Bygg, transport & logistik" },
  { icon: Store, label: "Handel, restaurang & service" },
  { icon: Users, label: "Tjänsteföretag & konsultbolag" },
  { icon: HeartPulse, label: "Vård, omsorg & friskvård" },
  { icon: Plane, label: "Företag med kollektivavtal" },
];

const why = [
  {
    title: "Vi ser till att lönen alltid blir rätt",
    body: "Vi är experter på lön och har djup kunskap om kollektivavtal, lagar och arbetsrättsliga regelverk. Vi reder ut även de mest komplexa lönefrågorna utan att tveka.",
  },
  {
    title: "Slipp retroaktiva ersättningar & straffavgifter",
    body: "Med korrekt lönehantering från start undviker ni dyrbara rättelser, kontrollavgifter och eftersläpande underlag till Skatteverket.",
  },
  {
    title: "Ni bygger ett starkt arbetsgivarvarumärke",
    body: "Löner som alltid är rätt och betalas i tid skapar förtroende hos medarbetarna — och gör er till en mer attraktiv arbetsgivare på en konkurrensutsatt marknad.",
  },
  {
    title: "Vi håller oss alltid uppdaterade",
    body: "Lagar, regler och kollektivavtal ändras hela tiden. Vi följer förändringarna noggrant så att ni inte behöver — och så att ingen detalj missas.",
  },
  {
    title: "Lönebackup när det krisar",
    body: "Har ni egen löneadministratör? Vår backup-tjänst säkerställer att lönen kommer ut även vid akut sjukdom, semester eller plötslig uppsägning.",
  },
  {
    title: "Tid över till att utveckla företaget",
    body: "Med modern och effektiv lönehantering frigör ni tid till det som faktiskt växer er affär — försäljning, kundkontakt och rekrytering.",
  },
];

const faqs = [
  {
    q: "Vad ingår i er löneadministration?",
    a: "Vi hanterar hela lönecykeln: löneberäkning, lönespecifikationer, utbetalningar, tidrapporter, semesterdagar, sjukskrivningar, reseräkningar, traktamenten, förmåner, utlägg och arbetsgivarintyg. Vi sköter också all rapportering till myndigheter — arbetsgivardeklaration på individnivå (AGI), kontrolluppgifter, FORA, Collectum och statistik till SCB. Allt enligt aktuellt kollektivavtal och svensk arbetsrätt.",
  },
  {
    q: "Vad kostar löneadministration per anställd och månad?",
    a: "Priset beror på antal anställda, kollektivavtal, komplexiteten i lönerna (skift, OB, ackord, provisioner) och om ni vill att vi hanterar reseräkningar och utlägg. Vi tar fram en konkret offert efter ett kostnadsfritt avstämningsmöte. Företag som har sin bokföring hos oss får ofta ett mer förmånligt paketpris.",
  },
  {
    q: "Vilka kollektivavtal kan ni hantera?",
    a: "Vi har erfarenhet av de flesta större avtal — bland annat Unionen, IF Metall, Byggnads, Transport, Handels, Hotell- och restaurangfacket, Vårdförbundet, Kommunal samt tjänstemannaavtal i privat sektor. Har ni ett ovanligt avtal går vi igenom det noggrant innan vi tar uppdraget så att inget missas.",
  },
  {
    q: "Vad är lönebackup och när är det aktuellt?",
    a: "Lönebackup är en tjänst för företag och organisationer som har egen löneadministratör men behöver en pålitlig backup vid akut frånvaro — sjukdom, semester, föräldraledighet eller plötslig uppsägning. Vi har all dokumentation och rutiner förberedda, och kliver in inom timmar för att säkerställa att löneutbetalningen sker i tid. Särskilt värdefullt för små och medelstora företag som inte har dubbla lönefunktioner.",
  },
  {
    q: "Hur skickar vi in tidrapporter och löneunderlag?",
    a: "Vi anpassar oss efter er befintliga process. Många kunder rapporterar via vårt digitala tidrapporteringssystem, andra mejlar Excel-underlag eller använder integrationer mot exempelvis Fortnox, Visma eller Hogia. Det viktigaste är att flödet fungerar smidigt för er — vi anpassar oss, inte tvärtom.",
  },
  {
    q: "Vad händer om en anställd har frågor om sin lön?",
    a: "Era anställda kan kontakta oss direkt med lönefrågor — vi har erfarenhet av att besvara allt från semesterberäkningar till skatteavdrag och förmåner. Det avlastar er HR och ger snabba, korrekta svar till medarbetarna. Föredrar ni att alla frågor går via er kontaktperson så löser vi det också.",
  },
];

const Loner = () => {
  const [openFaq, setOpenFaq] = useState<number>(0);

  return (
    <>
      <SEO
        title="Löneadministration & lönehantering — Lönespecialister | Cloud Redovisning"
        description="Komplett löneadministration: löneutbetalning, kollektivavtal, AGI-rapportering, tidrapporter, semester och lönebackup. Auktoriserade lönespecialister i Umeå & Stockholm. Boka kostnadsfritt möte."
      />

      {/* ───────── HERO (dark) ───────── */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden bg-black text-background">
        <div className="absolute inset-0 -z-10">
          <img
            src={hero}
            alt=""
            className="h-full w-full object-cover opacity-40 animate-slow-zoom"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/90 to-foreground/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground via-transparent to-foreground/60" />
        </div>
        <div className="absolute -top-32 -right-32 -z-10 h-[480px] w-[480px] rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 -z-10 h-[420px] w-[420px] rounded-full bg-primary-glow/10 blur-3xl" />

        <div className="container-page">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-primary-glow animate-fade-up backdrop-blur">
                <Sparkles className="h-3.5 w-3.5" /> Tjänst — Löneadministration
              </div>
              <h1 className="display-xl mt-8 text-background animate-fade-up delay-100 text-balance">
                Löner som{" "}
                <span className="italic text-primary-glow">
                  alltid blir rätt — i tid, varje gång
                </span>
                .
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-[1.7] font-light text-background/75 animate-fade-up delay-200">
                Vi sköter löneadministration för företag i Umeå, Stockholm och
                hela Sverige. Ni får rätt lön i rätt tid, trygg AGI-rapportering
                och stöd i allt från kollektivavtal till semester och
                lönebackup.
              </p>
              <div className="mt-10 flex flex-wrap gap-4 animate-fade-up delay-300">
                <Link
                  to="/kontakt-umea"
                  className="inline-flex items-center gap-3 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-background hover:text-foreground shadow-elevated"
                >
                  Boka kostnadsfritt möte <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#sa-jobbar-vi"
                  className="inline-flex items-center gap-3 rounded-full border border-background/30 bg-background/5 backdrop-blur px-7 py-3.5 text-sm font-medium text-background transition-all hover:bg-background hover:text-foreground"
                >
                  Se hur vi jobbar
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 animate-fade-in delay-200">
              <div className="relative">
                <div className="relative aspect-[4/5] overflow-hidden rounded-3xl shadow-elevated ring-1 ring-background/10">
                  <img
                    src={hero}
                    alt="Löneadministration utförd av lönespecialister"
                    className="h-full w-full object-cover animate-slow-zoom"
                    fetchpriority="high"
                  />
                </div>

                <div className="absolute -left-4 md:-left-10 top-10 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[210px]">
                  <div className="font-display text-3xl text-primary-glow">
                    100 %
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    Lönerna i tid sedan start
                  </div>
                </div>
                <div className="absolute -right-4 md:-right-8 bottom-12 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[210px]">
                  <div className="font-display text-3xl text-primary-glow">
                    AGI
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    Rapportering på individnivå
                  </div>
                </div>
                <div className="absolute -right-2 md:-right-4 -top-4 rounded-2xl border border-background/10 bg-foreground/80 backdrop-blur-md p-4 shadow-elevated max-w-[190px]">
                  <div className="font-display text-2xl text-primary-glow">
                    24h
                  </div>
                  <div className="text-xs text-background/70 mt-1 leading-snug">
                    Backup vid akut frånvaro
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── TRUST BAR ───────── */}
      <section className="bg-foreground text-background py-6">
        <div className="container-page">
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
            {trustItems.map((t) => (
              <div
                key={t}
                className="inline-flex items-center gap-2 text-sm text-background/75"
              >
                <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── WHAT'S INCLUDED ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow">— Vad ingår</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              Hela lönecykeln —{" "}
              <span className="italic text-primary">
                från tidrapport till utbetald lön
              </span>
              .
            </h2>
            <p className="lead mt-6">
              Vår löneavdelning består av specialister med många års erfarenhet
              av all typ av löneadministration. Tack vare vår breda kunskap om
              olika branscher anpassar vi oss efter ert företags storlek, behov
              och önskemål.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {included.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="group rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-elevated hover:-translate-y-1"
                >
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                    <Icon className="h-6 w-6 text-primary" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-lg font-medium text-foreground font-display mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-muted leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── STORY — narrative pillars from original site ───────── */}
      <StoryAlternating
        background="elevated"
        sectionEyebrow="Lönehantering på riktigt"
        sectionTitle={
          <>
            Lönespecialister, korrekta löner —{" "}
            <span className="italic text-primary">
              och en backup när det krisar
            </span>
            .
          </>
        }
        sectionLead="Tre pelare som gör vår löneadministration till en verklig avlastning för er HR och en tydlig fördel för era medarbetare."
        items={[
          {
            eyebrow: "01 — Specialister",
            title: "En löneavdelning av specialister",
            body: "Vår löneavdelning består av en handfull lönespecialister med många års erfarenhet av all typ av löneadministration. Vi hanterar inte enbart löneutbetalningar utan även tidrapporter, semesterdagar, sjukskrivningar, reseräkningar, traktamenten, förmåner, utlägg och arbetsgivarintyg — samt all rapportering till myndigheter. Vår breda branschkunskap gör att vi anpassar oss efter ditt företags storlek, behov och önskemål.",
            image: storyExperts,
          },
          {
            eyebrow: "02 — Förtroende",
            title: "Rätt löner skapar förtroende",
            body: "Som arbetsgivare ansvarar du för att avtal följs och att lönen betalas ut i rätt tid. När lönerna redovisas och rapporteras korrekt slipper du retroaktiva ersättningar och straffavgifter. Löner som alltid är rätt och kommer i tid skapar förtroende hos medarbetarna — och stärker ditt arbetsgivarvarumärke. Den tid vi sparar åt dig kan du lägga på att utveckla företaget istället.",
            image: storyTrust,
          },
          {
            eyebrow: "03 — Lönebackup",
            title: "Lönebackup när det krisar",
            body: "Backup-funktionen riktar sig till företag och organisationer som har en egen löneadministratör men behöver en pålitlig lösning vid akut frånvaro. Tjänsten är särskilt värdefull för små och medelstora företag som inte har resurser att hantera löneadministration internt under alla omständigheter — vi kliver in inom timmar så att lönen alltid kommer ut i tid.",
            image: storyBackup,
          },
        ]}
      />

      {/* ───────── HOW IT WORKS ───────── */}
      <section id="sa-jobbar-vi" className="py-20 md:py-32 bg-background-deep">
        <div className="container-page">
          <div className="grid gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="eyebrow">— Så jobbar vi</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Tre steg till en{" "}
                <span className="italic text-primary">
                  stressfri lönehantering
                </span>
                .
              </h2>
              <p className="lead mt-6">
                Övergången från er nuvarande lösning sker sömlöst — utan avbrott
                i löneutbetalningen.
              </p>

              <div className="mt-10 relative h-[320px] overflow-hidden rounded-3xl shadow-soft ring-1 ring-border">
                <img
                  src={specialistImage}
                  alt="Lönespecialist hos Cloud Redovisning"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>

              <Link
                to="/kontakt-umea"
                className="inline-flex items-center gap-3 mt-10 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-foreground"
              >
                Boka första mötet <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="lg:col-span-7">
              <div className="space-y-5">
                {steps.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div
                      key={s.title}
                      className="group relative rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-soft"
                    >
                      <div className="flex items-start gap-6">
                        <div className="relative flex-shrink-0">
                          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center text-primary-foreground shadow-soft">
                            <Icon className="h-7 w-7" strokeWidth={1.75} />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-foreground font-display">
                            {s.title}
                          </h3>
                          <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
                            {s.body}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── BACKUP STRIP ───────── */}
      <section className="py-16 md:py-20 bg-background border-y border-border">
        <div className="container-page">
          <div className="grid gap-10 md:grid-cols-12 items-center">
            <div className="md:col-span-5">
              <div className="eyebrow">— Lönebackup</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Akut backup när er{" "}
                <span className="italic text-primary">
                  egen lönehanterare faller bort
                </span>
                .
              </h2>
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-3 gap-4">
              {[
                {
                  icon: HeartPulse,
                  label: "Vid sjukdom",
                  body: "Vi kliver in inom timmar — lönen kommer ut i tid",
                },
                {
                  icon: Plane,
                  label: "Under semester",
                  body: "Planerad backup som garanterar kontinuitet",
                },
                {
                  icon: LifeBuoy,
                  label: "Vid uppsägning",
                  body: "Vi täcker upp tills ny lönespecialist är på plats",
                },
              ].map((d) => {
                const Icon = d.icon;
                return (
                  <div
                    key={d.label}
                    className="group rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary/30 hover:shadow-soft"
                  >
                    <Icon
                      className="h-6 w-6 text-primary mb-3"
                      strokeWidth={1.75}
                    />
                    <div className="font-display text-base text-foreground">
                      {d.label}
                    </div>
                    <div className="text-xs text-foreground-muted mt-1.5 leading-snug">
                      {d.body}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FOR WHOM ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow">— Vi sköter lönen för</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              Löneadministration för{" "}
              <span className="italic text-primary">
                alla typer av branscher
              </span>
              .
            </h2>
            <p className="lead mt-6">
              Vi har bred erfarenhet av kollektivavtal, branschspecifika regler
              och olika lönesystem — och anpassar oss efter er verklighet.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {forWhom.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className="flex items-center gap-4 rounded-2xl border border-border bg-card px-6 py-5 transition-all hover:border-primary/40 hover:shadow-soft"
                >
                  <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────── WHY CLOUD ───────── */}
      <section className="py-20 md:py-32 bg-background-deep">
        <div className="container-page">
          <div className="max-w-2xl mb-16">
            <div className="eyebrow">— Varför Cloud Redovisning</div>
            <h2 className="display-md mt-6 text-foreground text-balance">
              Rätt löner skapar{" "}
              <span className="italic text-primary">
                förtroende — och tid att utveckla bolaget
              </span>
              .
            </h2>
            <p className="lead mt-6">
              Som arbetsgivare ansvarar du för att avtal följs och löner betalas
              i tid. Med oss slipper du både risk och administration.
            </p>
          </div>

          <div className="grid gap-x-12 gap-y-10 md:grid-cols-2">
            {why.map((w) => (
              <div key={w.title} className="flex gap-5">
                <div className="flex-shrink-0 mt-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-foreground font-display">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
                    {w.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── TESTIMONIAL ───────── */}
      <section className="py-20 md:py-32 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 -z-0 opacity-10">
          <div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-primary-glow blur-3xl" />
        </div>
        <div className="container-page relative">
          <div className="max-w-3xl mx-auto text-center">
            <Quote
              className="h-12 w-12 text-primary mx-auto mb-8"
              strokeWidth={1.5}
            />
            <blockquote className="font-display text-2xl md:text-3xl leading-relaxed text-background/90 italic text-balance">
              Att överlåta lönerna till Cloud Redovisning har varit en av våra
              bästa beslut. Vi vet att lönerna alltid är rätt och i tid — och
              vår personal har en direkt kontakt för sina frågor. Det har lyft
              både medarbetarnöjdheten och frigjort tid för ledningen.
            </blockquote>
            <div className="mt-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-background/60">
              <span className="h-px w-8 bg-primary" />
              Nöjd kund — Tjänsteföretag i Umeå
            </div>
          </div>
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container-page">
          <div className="grid gap-12 lg:gap-16 lg:grid-cols-12 items-center">
            <div className="lg:col-span-4">
              <div className="eyebrow">— Vanliga frågor</div>
              <h2 className="display-md mt-6 text-foreground text-balance">
                Frågor om{" "}
                <span className="italic text-primary">löneadministration</span>.
              </h2>
              <p className="lead mt-6">
                Det vi hör mest från företag — med raka svar.
              </p>
              <div className="mt-10 relative h-[320px] overflow-hidden rounded-3xl shadow-soft">
                <img
                  src={faqImage}
                  alt="Vanliga frågor om löneadministration"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 ring-1 ring-foreground/5 rounded-3xl" />
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="space-y-3">
                {faqs.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div
                      key={f.q}
                      className={`rounded-2xl border transition-all duration-300 ${
                        open
                          ? "border-primary/30 bg-card shadow-soft"
                          : "border-border bg-card hover:border-border-strong"
                      }`}
                    >
                      <button
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        aria-expanded={open}
                        className="w-full flex items-center justify-between gap-6 text-left p-6 md:p-7"
                      >
                        <span className="font-display text-[1.5rem] tracking-[0.005em] text-foreground">
                          {f.q}
                        </span>
                        <span
                          className={`h-9 w-9 flex-shrink-0 rounded-full border flex items-center justify-center transition-all ${
                            open
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-border text-foreground"
                          }`}
                        >
                          {open ? (
                            <Minus className="h-4 w-4" />
                          ) : (
                            <Plus className="h-4 w-4" />
                          )}
                        </span>
                      </button>
                      <div hidden={!open} className="px-6 md:px-7 pb-7 -mt-2 text-sm text-foreground-muted leading-relaxed animate-fade-in">
                          {f.a}
                        </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── CTA ───────── */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container-page">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary to-accent p-10 md:p-16 text-primary-foreground shadow-elevated">
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />
            <div className="relative grid gap-10 md:grid-cols-12 items-center">
              <div className="md:col-span-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-primary-foreground/70">
                  — Nästa steg
                </div>
                <h2 className="display-md mt-6 text-primary-foreground text-balance">
                  Vill ni frigöra tid och slippa{" "}
                  <span className="italic">orosmoment kring lönen?</span>
                </h2>
                <p className="mt-6 max-w-xl text-primary-foreground/85 leading-relaxed">
                  Hör av er — vi visar gärna hur vi kan ta över hela eller delar
                  av ert lönearbete. Kostnadsfri genomgång utan förpliktelser.
                </p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-4 md:items-end">
                <Link
                  to="/kontakt-umea"
                  className="inline-flex items-center gap-3 rounded-full bg-background px-7 py-3.5 text-sm font-medium text-foreground transition-all hover:bg-foreground hover:text-background"
                >
                  Boka kostnadsfritt möte <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="tel:0703036345"
                  className="inline-flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-primary-foreground"
                >
                  <Phone className="h-3.5 w-3.5" /> 070-303 63 45
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RelatedLinksSection
        title="Bygg en stabil ekonomifunktion runt lönen."
        description="Lön fungerar bäst när bokföring, bokslut och rådgivning sitter ihop i samma arbetsflöde."
        links={[
          {
            to: "/bokforing",
            title: "Bokföring",
            body: "Koppla samman lön, bank, kvitton och rapporter i ett tydligt digitalt arbetssätt.",
          },
          {
            to: "/bokslut",
            title: "Bokslut & deklaration",
            body: "Säkerställ att löner, arbetsgivaravgifter och underlag landar rätt inför bokslut.",
          },
          {
            to: "/inhouse",
            title: "Lönebackup & interim",
            body: "När ni behöver tillfällig förstärkning i ekonomi- eller lönefunktionen på plats.",
          },
          {
            to: "/kontakt",
            title: "Kontakta oss",
            body: "Boka möte med Umeå eller Stockholm och få ett upplägg som matchar er lönecykel.",
          },
        ]}
      />

      {/* ───────── CONTACT FORM ───────── */}
      <ContactForm
        title="Kontakta oss för kostnadsfri rådgivning"
        subtitle="Skicka ett meddelande så återkommer vi snabbt med ett konkret förslag på upplägg."
      />
    </>
  );
};

export default Loner;
